<script lang="ts" setup>
import axios from 'axios'
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import useForm from '@/hooks/use-form'

interface Response {
    intended: string
}

const { t } = useI18n()
const usernameInput = ref<HTMLInputElement>()
const passwordInput = ref<HTMLInputElement>()
const { data, loading, errors, submit } = useForm({ username: '', password: '', rememberMe: false })

const usernameFocused = ref(false)
const passwordFocused = ref(false)
const showPassword = ref(false)
const showRipple = ref(false)

const hasUsernameValue = computed(() => data.username.length > 0 || usernameAutofilled.value)
const hasPasswordValue = computed(() => data.password.length > 0 || passwordAutofilled.value)

// Better autofill detection
const usernameAutofilled = ref(false)
const passwordAutofilled = ref(false)

function checkAutofill() {
    // Check if inputs have values (including autofilled)
    if (usernameInput.value?.value && usernameInput.value.value !== data.username) {
        data.username = usernameInput.value.value
        usernameAutofilled.value = true
    }
    if (passwordInput.value?.value && passwordInput.value.value !== data.password) {
        data.password = passwordInput.value.value
        passwordAutofilled.value = true
    }

    // Also check computed styles for autofill
    if (usernameInput.value) {
        const style = window.getComputedStyle(usernameInput.value)
        if (style.getPropertyValue('-webkit-autofill-shadow-start-color')) {
            usernameAutofilled.value = true
        }
    }
    if (passwordInput.value) {
        const style = window.getComputedStyle(passwordInput.value)
        if (style.getPropertyValue('-webkit-autofill-shadow-start-color')) {
            passwordAutofilled.value = true
        }
    }
}

async function login() {
    const response = await submit<Response>('post', window.route('login.attempt'))

    if (response) {
        window.location.href = response.intended
    }
}

function togglePasswordVisibility() {
    showPassword.value = !showPassword.value
}

function handleLoginClick() {
    // Trigger ripple animation
    showRipple.value = true
    setTimeout(() => {
        showRipple.value = false
    }, 600)

    // Execute login
    login()
}

onMounted(() => {
    usernameInput.value?.focus()
    // Retrieve CSRF token
    axios.get('/sanctum/csrf-cookie')

    // Check for autofill periodically and on input events
    setTimeout(() => checkAutofill(), 100)
    setTimeout(() => checkAutofill(), 500)
    setTimeout(() => checkAutofill(), 1000)
    setTimeout(() => checkAutofill(), 2000)

    // Add input event listeners to detect autofill
    usernameInput.value?.addEventListener('input', checkAutofill)
    passwordInput.value?.addEventListener('input', checkAutofill)

    // Check on focus/blur as well
    usernameInput.value?.addEventListener('focus', checkAutofill)
    usernameInput.value?.addEventListener('blur', checkAutofill)
    passwordInput.value?.addEventListener('focus', checkAutofill)
    passwordInput.value?.addEventListener('blur', checkAutofill)
})
</script>

<template>
    <!-- Header -->
    <div class="text-center mb-8">
        <div
            class="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-xl mb-4"
        >
            <MdiLogin class="w-8 h-8 text-dark-900" />
        </div>
        <h1 class="text-2xl font-bold text-dark-500 dark:text-dark-200 mb-2">Welcome back</h1>
        <p class="text-dark-300 dark:text-dark-200 text-sm">
            Sign in to access your logistics dashboard
        </p>
    </div>

    <!-- Login Form -->
    <form class="space-y-8" @submit.prevent="handleLoginClick">
        <!-- Username Field -->
        <div class="relative">
            <div class="relative">
                <MdiAccount
                    :class="{
                        'text-yellow-500': usernameFocused || hasUsernameValue,
                        'text-dark-300': !usernameFocused && !hasUsernameValue,
                        'text-red-500': errors.username,
                    }"
                    class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 z-10"
                />
                <input
                    id="username"
                    ref="usernameInput"
                    v-model="data.username"
                    :class="{
                        'border-yellow-500 ring-2 ring-yellow-200 dark:ring-yellow-400/30':
                            usernameFocused,
                        'border-dark-400': !usernameFocused && !errors.username,
                        'border-red-500 ring-2 ring-red-200 dark:ring-red-400/30': errors.username,
                    }"
                    class="w-full h-12 pl-12 pr-4 text-dark-500 dark:text-dark-200 bg-primary-100 dark:bg-dark-700 border rounded-xl transition-all duration-300 focus:outline-none peer"
                    type="text"
                    autocomplete="username"
                    placeholder=" "
                    @blur="usernameFocused = false"
                    @focus="usernameFocused = true"
                />
                <label
                    for="username"
                    :class="{
                        'text-yellow-500 -top-2 left-10 scale-75':
                            usernameFocused || hasUsernameValue,
                        'text-dark-400 top-3 left-12 scale-100':
                            !usernameFocused && !hasUsernameValue,
                        'text-red-500': errors.username,
                    }"
                    class="absolute font-medium bg-primary-100 dark:bg-dark-700 px-2 transition-all duration-300 pointer-events-none z-10"
                >
                    {{ t('username') }}
                </label>
            </div>
            <div v-if="errors.username" class="text-red-500 text-xs mt-1">
                {{ errors.username }}
            </div>
        </div>

        <!-- Password Field -->
        <div class="relative">
            <div class="relative">
                <MdiLock
                    :class="{
                        'text-yellow-500': passwordFocused || hasPasswordValue,
                        'text-dark-300': !passwordFocused && !hasPasswordValue,
                        'text-red-500': errors.password,
                    }"
                    class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 z-10"
                />
                <input
                    id="password"
                    ref="passwordInput"
                    v-model="data.password"
                    :type="showPassword ? 'text' : 'password'"
                    :class="{
                        'border-yellow-500 ring-2 ring-yellow-200 dark:ring-yellow-400/30':
                            passwordFocused,
                        'border-dark-400': !passwordFocused && !errors.password,
                        'border-red-500 ring-2 ring-red-200 dark:ring-red-400/30': errors.password,
                    }"
                    class="w-full h-12 pl-12 pr-12 text-dark-500 dark:text-dark-200 bg-primary-100 dark:bg-dark-700 border rounded-xl transition-all duration-300 focus:outline-none peer"
                    autocomplete="current-password"
                    placeholder=" "
                    @blur="passwordFocused = false"
                    @focus="passwordFocused = true"
                />
                <label
                    for="password"
                    :class="{
                        'text-yellow-500 -top-2 left-10 scale-75':
                            passwordFocused || hasPasswordValue,
                        'text-dark-400 top-3 left-12 scale-100':
                            !passwordFocused && !hasPasswordValue,
                        'text-red-500': errors.password,
                    }"
                    class="absolute font-medium bg-primary-100 dark:bg-dark-700 px-2 transition-all duration-300 pointer-events-none z-10"
                >
                    {{ t('password') }}
                </label>
                <button
                    type="button"
                    class="absolute right-4 top-1/2 transform -translate-y-1/2 text-dark-300 hover:text-yellow-500 dark:hover:text-yellow-400 transition-colors duration-300 z-10"
                    @click="togglePasswordVisibility"
                >
                    <MdiEye v-if="!showPassword" class="w-5 h-5" />
                    <MdiEyeOff v-else class="w-5 h-5" />
                </button>
            </div>
            <div v-if="errors.password" class="text-red-500 text-xs mt-1">
                {{ errors.password }}
            </div>
        </div>

        <!-- Remember Me -->
        <div class="flex items-center justify-between">
            <label class="flex items-center space-x-3 cursor-pointer">
                <input
                    v-model="data.rememberMe"
                    type="checkbox"
                    class="w-4 h-4 text-yellow-500 bg-primary-100 dark:bg-dark-700 border-dark-400 rounded focus:ring-yellow-500 focus:ring-2"
                />
                <span class="text-sm text-dark-500 dark:text-dark-200">Remember me</span>
            </label>

            <RouterLink
                class="text-sm text-yellow-600 hover:text-yellow-500 dark:text-yellow-400 dark:hover:text-yellow-300 transition-colors duration-200 font-medium"
                to="/forgot-password"
            >
                {{ t('forgotPassword') }}
            </RouterLink>
        </div>

        <!-- Login Button -->
        <button
            type="button"
            :disabled="loading"
            :class="{
                'opacity-75 cursor-not-allowed': loading,
                'hover:bg-yellow-400 hover:shadow-lg hover:shadow-yellow-500/25': !loading,
            }"
            class="w-full h-12 bg-yellow-500 text-dark-900 font-bold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
            @click="handleLoginClick"
        >
            <div
                v-if="loading"
                class="w-5 h-5 border-2 border-dark-900/40 border-t-dark-900 rounded-full animate-spin"
            ></div>
            <template v-else>
                <MdiLogin class="w-5 h-5" />
                <span>{{ t('login') }}</span>
            </template>
        </button>
    </form>
</template>

<style scoped>
@keyframes fade-in-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes shake {
    0%,
    100% {
        transform: translateX(0);
    }
    25% {
        transform: translateX(-4px);
    }
    75% {
        transform: translateX(4px);
    }
}

.animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
}

.animate-shake {
    animation: shake 0.4s ease-in-out;
}

/* Autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    -webkit-background-clip: padding-box;
    -webkit-text-fill-color: #181818 !important;
    transition: background-color 5000s ease-in-out 0s !important;
    box-shadow: inset 0 0 20px 20px rgba(255, 255, 255, 0.9) !important;
    border-radius: 12px !important;
}

input:-webkit-autofill::first-line {
    color: #181818 !important;
}

/* Dark mode autofill */
.dark input:-webkit-autofill,
.dark input:-webkit-autofill:hover,
.dark input:-webkit-autofill:focus,
.dark input:-webkit-autofill:active {
    -webkit-text-fill-color: #c5c5c5 !important;
    box-shadow: inset 0 0 20px 20px #222222 !important;
}

.dark input:-webkit-autofill::first-line {
    color: #c5c5c5 !important;
}

/* Focus caret color */
input:focus {
    caret-color: #ffd128 !important;
}

/* Ripple animation for login button */
@keyframes ripple {
    0% {
        transform: scaleX(0);
        opacity: 0.5;
    }
    50% {
        opacity: 0.3;
    }
    100% {
        transform: scaleX(1);
        opacity: 0;
    }
}

.animate-ripple {
    animation: ripple 0.6s ease-out;
    transform-origin: left;
}
</style>
